import { supabase } from '../lib/supabase';

/**
 * Service for managing monthly sales performance data
 * Handles snake_case (database) ↔ camelCase (React) conversion
 */

// Convert database row to camelCase for React
const toReactFormat = (row) => ({
  id: row?.id,
  userId: row?.user_id,
  trackingMonth: row?.tracking_month,
  entryMode: row?.entry_mode || 'advanced',
  totalSales: row?.total_sales,
  existingClientSales: row?.existing_client_sales,
  newClientSales: row?.new_client_sales,
  existingClientRevenue: parseFloat(row?.existing_client_revenue || 0),
  newClientRevenue: parseFloat(row?.new_client_revenue || 0),
  averageSaleAmount: parseFloat(row?.average_sale_amount || 0),
  productSalesRevenue: parseFloat(row?.product_sales_revenue || 0),
  grossProfitPercentage: parseFloat(row?.gross_profit_percentage || 50),
  monthlyProfit: parseFloat(row?.monthly_profit || 0),
  googleAdsExpense: parseFloat(row?.google_ads_expense || 0),
  otherMonthlyCosts: parseFloat(row?.other_monthly_costs || 0),
  monthlyNetRevenue: parseFloat(row?.monthly_net_revenue || 0),
  googleAdsRecoveryPercentage: parseFloat(row?.google_ads_recovery_percentage || 0),
  roas: parseFloat(row?.roas || 0),
  currency: row?.currency,
  notes: row?.notes,
  createdAt: row?.created_at,
  updatedAt: row?.updated_at
});

// Convert React data to snake_case for database
const toDbFormat = (data) => {
  // Convert YYYY-MM format to YYYY-MM-01 for PostgreSQL DATE type
  // Only append day if not already present (check for pattern YYYY-MM-DD)
  let trackingMonth = data?.trackingMonth;
  
  if (trackingMonth) {
    // Check if date already has day component (format: YYYY-MM-DD)
    const hasDay = /^\d{4}-\d{2}-\d{2}$/?.test(trackingMonth);
    
    if (!hasDay) {
      // If only YYYY-MM format, append -01 for first day of month
      trackingMonth = `${trackingMonth}-01`;
    }
  }

  return {
    user_id: data?.userId,
    tracking_month: trackingMonth,
    entry_mode: data?.entryMode || 'advanced',
    total_sales: data?.totalSales,
    existing_client_sales: data?.existingClientSales,
    new_client_sales: data?.newClientSales,
    existing_client_revenue: data?.existingClientRevenue,
    new_client_revenue: data?.newClientRevenue,
    average_sale_amount: data?.averageSaleAmount,
    product_sales_revenue: data?.productSalesRevenue,
    gross_profit_percentage: data?.grossProfitPercentage,
    google_ads_expense: data?.googleAdsExpense,
    other_monthly_costs: data?.otherMonthlyCosts,
    currency: data?.currency,
    notes: data?.notes
  };
};

// Helper function to get the effective user ID (handles admin impersonation)
const getEffectiveUserId = async () => {
  const { data: { user } } = await supabase?.auth?.getUser();
  if (!user) throw new Error('Not authenticated');

  // Check for admin impersonation context
  const impersonationData = localStorage.getItem('admin_impersonation');
  if (impersonationData) {
    try {
      const { impersonatedUserId } = JSON.parse(impersonationData);
      // Return impersonated user's ID if impersonating
      return impersonatedUserId;
    } catch (error) {
      console.error('Error parsing impersonation data:', error);
      // Fall back to current user if parsing fails
      return user?.id;
    }
  }

  // Return current user ID if not impersonating
  return user?.id;
};

export const salesPerformanceService = {
  /**
   * Get all sales performance records for current user (or impersonated user)
   * @param {number} limit - Optional limit for number of months
   * @returns {Promise<Array>} Array of sales performance records
   */
  async getAll(limit = 24) {
    try {
      const effectiveUserId = await getEffectiveUserId();

      const { data, error } = await supabase
        ?.from('monthly_sales_performance')
        ?.select('*')
        ?.eq('user_id', effectiveUserId)
        ?.order('tracking_month', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return data?.map(toReactFormat);
    } catch (err) {
      console.error('Error fetching sales performance:', err);
      throw err;
    }
  },

  /**
   * Get sales performance for specific month (supports admin impersonation)
   * @param {string} month - Month in YYYY-MM format
   * @returns {Promise<Object|null>} Sales performance record or null
   */
  async getByMonth(month) {
    try {
      const effectiveUserId = await getEffectiveUserId();

      const { data, error } = await supabase
        ?.from('monthly_sales_performance')
        ?.select('*')
        ?.eq('user_id', effectiveUserId)
        ?.eq('tracking_month', `${month}-01`)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return data ? toReactFormat(data) : null;
    } catch (err) {
      console.error('Error fetching month data:', err);
      throw err;
    }
  },

  /**
   * Create new sales performance record
   * @param {Object} performanceData - Sales performance data
   * @returns {Promise<Object>} Created record
   */
  async create(performanceData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const dbData = toDbFormat({ ...performanceData, userId: user?.id });

      const { data, error } = await supabase?.from('monthly_sales_performance')?.insert(dbData)?.select()?.single();

      if (error) throw error;
      return toReactFormat(data);
    } catch (err) {
      console.error('Error creating sales performance:', err);
      throw err;
    }
  },

  /**
   * Update existing sales performance record
   * @param {string} id - Record ID
   * @param {Object} updates - Updated fields
   * @returns {Promise<Object>} Updated record
   */
  async update(id, updates) {
    try {
      const dbData = toDbFormat(updates);

      const { data, error } = await supabase?.from('monthly_sales_performance')?.update(dbData)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return toReactFormat(data);
    } catch (err) {
      console.error('Error updating sales performance:', err);
      throw err;
    }
  },

  /**
   * Delete sales performance record
   * @param {string} id - Record ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      const { error } = await supabase?.from('monthly_sales_performance')?.delete()?.eq('id', id);

      if (error) throw error;
    } catch (err) {
      console.error('Error deleting sales performance:', err);
      throw err;
    }
  },

  /**
   * Get aggregated insights for overview dashboard (supports admin impersonation)
   * @param {number} months - Number of months to analyze
   * @returns {Promise<Object>} Aggregated insights
   */
  async getInsights(months = 12) {
    try {
      const effectiveUserId = await getEffectiveUserId();

      const { data, error } = await supabase
        ?.from('monthly_sales_performance')
        ?.select('*')
        ?.eq('user_id', effectiveUserId)
        ?.order('tracking_month', { ascending: true });

      if (error) throw error;

      const totalRevenue = data?.reduce((sum, record) => 
        sum + (parseFloat(record?.existing_client_revenue || 0) + parseFloat(record?.new_client_revenue || 0)), 0
      );

      const totalExpenses = data?.reduce((sum, record) => 
        sum + parseFloat(record?.google_ads_expense || 0) + parseFloat(record?.other_monthly_costs || 0), 0
      );

      const totalPPCFees = data?.reduce((sum, record) => 
        sum + parseFloat(record?.google_ads_expense || 0), 0
      );

      const totalAgencyFees = data?.reduce((sum, record) => 
        sum + parseFloat(record?.other_monthly_costs || 0), 0
      );

      // Calculate average ROAS across all months
      const roasValues = data
        ?.filter(record => parseFloat(record?.roas || 0) > 0)
        ?.map(record => parseFloat(record?.roas || 0));
      
      const averageROAS = roasValues?.length > 0 
        ? roasValues?.reduce((sum, val) => sum + val, 0) / roasValues?.length 
        : 0;

      // Calculate ROAS trend (comparing last 3 months vs previous 3 months)
      let roasTrend = 0;
      if (data?.length >= 6) {
        const lastThree = data?.slice(-3);
        const prevThree = data?.slice(-6, -3);
        
        const lastThreeAvgROAS = lastThree?.reduce((sum, r) => sum + parseFloat(r?.roas || 0), 0) / 3;
        const prevThreeAvgROAS = prevThree?.reduce((sum, r) => sum + parseFloat(r?.roas || 0), 0) / 3;
        
        if (prevThreeAvgROAS > 0) {
          roasTrend = ((lastThreeAvgROAS - prevThreeAvgROAS) / prevThreeAvgROAS) * 100;
        }
      }

      // Calculate revenue trend - FIXED: Using actual total revenue instead of product_sales_revenue
      let revenueTrend = 0;
      if (data?.length >= 2) {
        const lastMonth = data?.[data?.length - 1];
        const previousMonth = data?.[data?.length - 2];
        
        // Calculate total revenue for each month (existing + new client revenue)
        const lastMonthRevenue = parseFloat(lastMonth?.existing_client_revenue || 0) + parseFloat(lastMonth?.new_client_revenue || 0);
        const previousMonthRevenue = parseFloat(previousMonth?.existing_client_revenue || 0) + parseFloat(previousMonth?.new_client_revenue || 0);
        
        if (previousMonthRevenue > 0) {
          revenueTrend = ((lastMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100;
        }
      }

      // Calculate profit margin
      const netProfit = totalRevenue - totalExpenses;
      const averageProfitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

      return {
        totalRevenue,
        totalExpenses,
        totalPPCFees,
        totalAgencyFees,
        totalNewClients: data?.reduce((sum, record) => sum + (parseInt(record?.new_client_sales) || 0), 0),
        totalExistingClients: data?.reduce((sum, record) => sum + (parseInt(record?.existing_client_sales) || 0), 0),
        totalNewClientRevenue: data?.reduce((sum, record) => sum + (parseFloat(record?.new_client_revenue) || 0), 0),
        totalExistingClientRevenue: data?.reduce((sum, record) => sum + (parseFloat(record?.existing_client_revenue) || 0), 0),
        averageProfitMargin,
        revenueTrend, // FIXED: Now using calculated value instead of hardcoded 0
        averageROAS,
        roasTrend
      };
    } catch (err) {
      console.error('Error calculating insights:', err);
      throw err;
    }
  },

  /**
   * Get monthly trend data for charts (supports admin impersonation)
   * @param {number} months - Number of months
   * @returns {Promise<Array>} Trend data
   */
  async getTrendData(months = 12) {
    try {
      const effectiveUserId = await getEffectiveUserId();

      const { data, error } = await supabase
        ?.from('monthly_sales_performance')
        ?.select('*')
        ?.eq('user_id', effectiveUserId)
        ?.order('tracking_month', { ascending: true });

      if (error) throw error;

      return data?.map(record => ({
        month: new Date(record?.tracking_month)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        revenue: parseFloat(record?.existing_client_revenue || 0) + parseFloat(record?.new_client_revenue || 0),
        expenses: parseFloat(record?.google_ads_expense || 0) + parseFloat(record?.other_monthly_costs || 0),
        netRevenue: parseFloat(record?.monthly_net_revenue || 0),
        existingClients: parseInt(record?.existing_client_sales || 0),
        newClients: parseInt(record?.new_client_sales || 0),
        roas: parseFloat(record?.roas || 0)
      })) || [];
    } catch (err) {
      console.error('Error getting trend data:', err);
      throw err;
    }
  }
};